import { Flame, Snowflake } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import type { StreakState } from "@/types/streak"

/**
 * OKS-515 — §12 per-state flame hero.
 *
 * Full-bleed gradient block with a big flame/snowflake glyph, the "N days"
 * count (40px), and a per-state subline (UX Spec §3). The glow is a static
 * CSS `drop-shadow` on the glyph; the at-risk hero adds a subtle pulse gated
 * by `motion-safe:` so it respects `prefers-reduced-motion`. No images, no JS
 * animation.
 *
 * Per state:
 *   - active  → orange, flame, subline = personal best (when > 0)
 *   - at_risk → red, flame + pulse, subline = "Ends tonight — post in Nh…"
 *   - frozen  → ice-blue, snowflake, subline = "Frozen — safe today"
 *               (presentational only — never reached from server data this
 *               ticket; see OKS-516)
 *   - broken  → grey, greyed flame, subline = "Your streak ended" (+ personal
 *               best when > 0)
 *
 * Meaning is carried by the number + subline text, never colour alone (UX
 * Spec §6); the glyph is `aria-hidden`. The number + subline are real text
 * with ≥4.5:1 contrast (white on saturated fills).
 */
interface StreakHeroProps {
  state: StreakState
  days: number
  longestDays: number
  /** Whole hours to brand-tz midnight; required (and used) only when at_risk. */
  hoursToMidnight: number | null
}

const GRADIENTS: Record<StreakState, string> = {
  active: "bg-[#ff5c00]",
  at_risk: "bg-gradient-to-b from-red-400 to-red-600",
  frozen: "bg-gradient-to-b from-sky-400 to-blue-600",
  broken: "bg-gradient-to-b from-slate-400 to-slate-600",
}

export function StreakHero({
  state,
  days,
  longestDays,
  hoursToMidnight,
}: StreakHeroProps) {
  const { t } = useTranslation()

  const Glyph = state === "frozen" ? Snowflake : Flame

  return (
    <section
      data-testid="streak-hero"
      data-state={state}
      className={cn(
        "flex flex-col items-center gap-2 px-6 py-10 text-center text-white",
        GRADIENTS[state],
      )}
    >
      <Glyph
        aria-hidden="true"
        className={cn(
          "size-9 drop-shadow-[0_2px_12px_rgba(255,255,255,0.55)]",
          state === "at_risk" && "motion-safe:animate-pulse",
          state === "broken" && "opacity-70",
        )}
      />
      <p
        className="text-[40px] leading-none font-bold"
        data-testid="streak-hero-count"
      >
        {t("streak.detail.hero.days", { count: days })}
      </p>
      <StreakHeroSubline
        state={state}
        longestDays={longestDays}
        hoursToMidnight={hoursToMidnight}
      />
    </section>
  )
}

function StreakHeroSubline({
  state,
  longestDays,
  hoursToMidnight,
}: Omit<StreakHeroProps, "days">) {
  const { t } = useTranslation()

  if (state === "at_risk") {
    return (
      <p className="text-sm text-white/90" data-testid="streak-hero-subline">
        {t("streak.detail.hero.at_risk", { count: hoursToMidnight ?? 1 })}
      </p>
    )
  }

  if (state === "frozen") {
    return (
      <p className="text-sm text-white/90" data-testid="streak-hero-subline">
        {t("streak.detail.hero.frozen")}
      </p>
    )
  }

  if (state === "broken") {
    return (
      <div className="flex flex-col gap-0.5" data-testid="streak-hero-subline">
        <p className="text-sm text-white/90">
          {t("streak.detail.hero.broken")}
        </p>
        {longestDays > 0 && (
          <p className="text-sm text-white/80">
            {t("streak.detail.hero.personal_best", { count: longestDays })}
          </p>
        )}
      </div>
    )
  }

  // active — personal best (omit the line for a never-best 0, defensive)
  if (longestDays <= 0) return null
  return (
    <p className="text-sm text-white/90" data-testid="streak-hero-subline">
      {t("streak.detail.hero.personal_best", { count: longestDays })}
    </p>
  )
}
